// frontend/src/modules/invoicing/components/CreditNoteDialog.tsx
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { formatMoney } from '../../../design-system/format';
import { invoicesApi } from '../api';
import type { Invoice } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  invoice: Invoice;
  currency: string;
  onIssued: (related: Invoice) => void;
}

export function CreditNoteDialog({ open, onOpenChange, invoice, currency, onIssued }: Props) {
  const navigate = useNavigate();
  const creditable = Math.max(Number(invoice.total) - Number(invoice.amount_credited ?? 0), 0);
  const [amount, setAmount] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) { setAmount(''); setNotes(''); }
  }, [open]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const amt = Number(amount);
    if (!(amt > 0)) { toast.error('Enter a positive amount'); return; }
    if (amt > creditable + 0.005) {
      toast.error(`Credit cannot exceed ${formatMoney(creditable, currency)}`);
      return;
    }
    setBusy(true);
    try {
      const { credit_note, related_invoice } = await invoicesApi.createCreditNote(invoice.id, {
        amount: amt,
        notes: notes.trim() || null,
      });
      toast.success(`Credit note ${credit_note.invoice_number} issued`);
      onIssued(related_invoice);
      onOpenChange(false);
      navigate(`/dashboard/invoices/${credit_note.id}`);
    } catch (err: any) {
      toast.error('Could not issue credit note', { description: err.message ?? String(err) });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Issue credit note — {invoice.invoice_number}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          <div className="grid gap-1.5">
            <Label>Credit amount<span className="text-destructive ml-0.5">*</span></Label>
            <Input type="number" min="0.01" step="0.01" max={creditable} value={amount} onChange={(e) => setAmount(e.target.value)} required autoFocus />
            <p className="text-xs text-muted-foreground">
              Up to <span className="font-mono">{formatMoney(creditable, currency)}</span>. The credit reduces this
              invoice's amount due and gets its own CN-numbered document.
            </p>
          </div>
          <div className="grid gap-1.5">
            <Label>Reason / notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Returned goods, goodwill, billing correction…" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Issuing…' : 'Issue credit note'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
